import { useEffect, useState } from "react";
import { Badge, Button, Dropdown } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { usesInspectionWorkspace } from "../../inspection/index.jsx";
import {
  FaCircle,
  FaCamera,
  FaCog,
  FaFileAlt,
  FaPrint,
  FaSignOutAlt,
} from "react-icons/fa";
import Hint from "../shared/Hint.jsx";
import ThemeSelector from "./ThemeSelector.jsx";
import PageDefaultsMenu from "./PageDefaultsMenu.jsx";
import { logout, getCriticalErrorCount, isAbortError } from "../../services/apiClient.js";
import { useSystemSetting } from "../shared/useSystemSetting.js";

// The Reports menu = THE COMPLETE report surface (Robert 2026-07-17): every
// planned report is listed, stubs included, and each earns its green dot as it's
// built, so the full column of dots is the finish line.
//
// THE MASTER SWITCH IS GONE FROM HERE (Robert 2026-08-07: "open all reports …
// untie from master reports setting"). It was the phase-1 verification pen — a
// report not yet checked read as "not built yet" rather than showing work in
// progress. It came off the Reports group and Phonebook on 2026-08-05, and this
// is the rest of it: the tail of Worklists and both Find pages.
//
// So `alwaysOn` is gone rather than set on all fifteen, and the dot is now just
// "is it built". A flag every row carries is not a flag. Untying the menu was
// never enough on its own either — each PAGE gated itself as well, and the
// setting, its endpoint and components/reports/ReportsGate.jsx went with it.
// Marketing Phone Calls keeps `marketing_calls_enabled`, which is a different
// thing entirely: an operating control admins use during busy periods.
//
// (Jobs Logged / Accepted — like the Dashboard header).
// Grouped by INTENT (Robert 2026-07-17): Reports = read the numbers ·
// Worklists = go do the work (rows fall off as it's done) · Find = look
// something up. Marketing rides in Worklists, hidden while its setting is off.
const REPORT_ITEMS = [
  { header: "Reports" },
  { label: "Jobs Logged / Accepted", path: "/reports/logged-accepted", implemented: true },
  { label: "File Life", path: "/reports/file-life", implemented: true },
  { label: "KPI Report", path: "/reports/kpi", implemented: true },
  { label: "Strike Rate", path: "/reports/strike-rate", implemented: true },
  { label: "Job Schedule", path: "/reports/job-schedule", implemented: true },
  { label: "Problem & Important Jobs", path: "/reports/important-jobs", implemented: true },
  { label: "Adverse Feedback", path: "/reports/adverse-feedback", implemented: true },
  { label: "Positive Feedback", path: "/reports/positive-feedback", implemented: true },
  { label: "QCC Inspections", path: "/reports/qcc", implemented: true },
  { label: "Predictive Invoicing", path: "/rdr/predictive", implemented: true },
  { label: "Invoicing by Month", path: "/reports/invoicing-by-month", implemented: true },
  { label: "Work Given to Contractors", path: "/reports/work-given", implemented: true },
  { label: "Performance Report", path: "/reports/performance", implemented: true },
  { label: "Completions", path: "/rdr/completions", implemented: true },
  { header: "Worklists" },
  { label: "Overruns", path: "/reports/overruns", implemented: true },
  { label: "Non Approved Contractor Invoices", path: "/reports/non-approved-contractor-invoices", implemented: true },
  { label: "H&S Order Worklist", path: "/reports/hs-order-worklist", implemented: true },
  { label: "Contractors Report", path: "/reports/contractors", implemented: true },
  { label: "Contractors Complete Needs Checking", path: "/reports/contractors-complete-needs-checking", implemented: true },
  { label: "Marketing Phone Calls", path: "/marketing", implemented: true, marketing: true },
  { header: "Find" },
  { label: "Find Invoice", path: "/reports/find-invoice", implemented: true },
  { label: "Find Order", path: "/reports/find-order", implemented: true },
  { label: "Phonebook", path: "/reports/phonebook", implemented: true },
  // The office's own books, not job data. Office Expenses is the register KR
  // kept inside job 69773 (docs/business-rules/office-expenses.md). Everyone
  // in the office sees and adds (KS 2026-09-08); the approval trail is the
  // administrators' and the route enforces that.
  { header: "Office" },
  { label: "Office Expenses", path: "/office-expenses", implemented: true },
];

function UserMenu({ user, setCurrentUser }) {
  const navigate = useNavigate();
  const marketingOn = useSystemSetting("marketing_calls_enabled");

  // The "live" dot (Robert 2026-07-16): shown when clicking the item gives the
  // REAL report. It used to mean "built AND released by the master switch"; with
  // the switch untied it means simply "built", which is the only thing left that
  // can vary — every item that is implemented is now reachable. Stubs still show
  // no dot, so the full column of dots remains the finish line.
  //
  // The re-fetch that used to hang off the dropdown's onToggle went with it: it
  // existed so the dots tracked someone flipping the switch in System Data
  // mid-session, and there is no longer a switch to flip.
  const liveDot = (item) =>
    item.implemented ? (
      <FaCircle className="text-success ms-2" style={{ fontSize: "0.35rem", verticalAlign: "middle" }} />
    ) : null;

  // The list is never filtered (Robert 2026-07-22): every planned report shows,
  // stubs included, so the menu is a preview of what is coming as well as a way
  // into what is here. The dots alone say which is which.

  async function handleLogout() {
    if (!window.confirm("Are you sure you want to logout?")) return;

    await logout();
    setCurrentUser(null);
    navigate("/login");
  }

  // Two rows, table feel (Robert 2026-07-22): label column · content · a
  // RIGHT-ALIGNED icon group of three per row, so the icon columns line up
  // between rows. NAME = who + everything personal (logout · theme · page
  // defaults) · TOOLS = go somewhere / do something. Spacing from gaps only.
  const rowLabel = (text) => (
    <span
      className="small text-uppercase text-muted"
      style={{ letterSpacing: "0.05em", width: 52, flexShrink: 0 }}
    >
      {text}
    </span>
  );

  return (
    <div className="user-menu d-flex flex-column row-gap-2">
      <div className="d-flex align-items-center gap-2">
        {/* "User" not "Name" (Robert 2026-07-22) — the row holds everything
            ABOUT the user: who they are, logout, theme, page defaults. */}
        {rowLabel("User")}
        <span className="fw-semibold">{user.full_name}</span>
        <CriticalErrorBadge user={user} />
        <span className="ms-auto d-flex align-items-center gap-2">
          <Hint placement="bottom" label="Logout">
            <Button variant="outline-dark" size="sm" onClick={handleLogout}>
              <FaSignOutAlt />
            </Button>
          </Hint>
          <ThemeSelector user={user} setCurrentUser={setCurrentUser} />
          <PageDefaultsMenu user={user} setCurrentUser={setCurrentUser} />
        </span>
      </div>

      <div className="d-flex align-items-center gap-2">
        {rowLabel("Tools")}
        <span className="ms-auto d-flex align-items-center gap-2">
        {usesInspectionWorkspace(user) ? (
          <Hint placement="bottom" label="My Inspections">
            <Button variant="outline-success" size="sm" onClick={() => navigate("/inspection")}>
              <FaCamera />
            </Button>
          </Hint>
        ) : null}
        <Hint placement="bottom" label="Reports">
          <Dropdown>
            <Dropdown.Toggle
              variant="outline-dark"
              id="reports-dropdown"
              aria-label="Reports"
              className="btn btn-sm no-caret"
            >
              <FaFileAlt />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {REPORT_ITEMS.map((item, i) =>
                item.admin && !user?.is_admin ? null : item.header ? (
                  <Dropdown.Header key={`hdr-${i}`}>{item.header}</Dropdown.Header>
                ) : item.marketing && marketingOn === false ? null : (
                  <Dropdown.Item key={item.path} onClick={() => navigate(item.path)}>
                    {item.label}
                    {liveDot(item)}
                  </Dropdown.Item>
                )
              )}
            </Dropdown.Menu>
          </Dropdown>
        </Hint>

        {/* Straight to System Data while it's the only entry (Robert
            2026-07-17) — revert to a dropdown if a second item arrives. */}
        <Hint placement="bottom" label="System Data">
          <Button
            variant="outline-info"
            size="sm"
            onClick={() => navigate("/admin/system-data")}
          >
            <FaCog />
          </Button>
        </Hint>

        <Hint placement="bottom" label="Print">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => window.print()}
          >
            <FaPrint />
          </Button>
        </Hint>
        </span>
      </div>
    </div>
  );
}

export default UserMenu;

// THE RED PILL (Robert 2026-08-24, the intent recorded in errorLog.js when
// 'critical' was introduced). Criticals are the severity that must not wait
// for somebody to wander into System Data — today that means a scheduled task
// died overnight, and nobody would know until the next time they looked.
//
// ADMINS ONLY, because the badge's whole job is to send someone to the Error
// Log and that is admin-gated (confirmed by mimicking an admin: Karen Renall
// sees Error Log but not Scheduler or Owner Data). Showing it to a user who
// then hits a wall would be worse than not showing it.
//
// Clearing the rows is the acknowledgement — there is no separate "seen"
// flag, and a 7-day window means an old incident nobody deleted stops
// shouting eventually.
function CriticalErrorBadge({ user }) {
  const navigate = useNavigate();
  const [state, setState] = useState(null);

  useEffect(() => {
    if (!user?.is_admin) return undefined;
    const controller = new AbortController();
    let alive = true;
    const load = async () => {
      try {
        const data = await getCriticalErrorCount({ signal: controller.signal });
        if (alive) setState(data);
      } catch (err) {
        // A badge that cannot count is simply absent — never an error message
        // in the header, and never a blank where a number should be.
        if (!isAbortError(err) && alive) setState(null);
      }
    };
    load();
    // A long-lived SPA session may never remount, so re-ask occasionally;
    // one indexed count is cheap and five minutes is far inside the window
    // that matters for something discovered overnight.
    const timer = setInterval(load, 5 * 60 * 1000);
    return () => {
      alive = false;
      controller.abort();
      clearInterval(timer);
    };
  }, [user?.is_admin]);

  if (!user?.is_admin || !state?.count) return null;
  const n = state.count;
  return (
    <Hint
      placement="bottom"
      label={`${n} critical error${n === 1 ? "" : "s"} in the last ${state.days} days. Something the system runs on its own has failed (usually an after-hours task), so it may not have happened at all. Click to open the Error Log. If you are not sure what it means, send the System Owner a screenshot.`}
    >
      <Badge
        bg="danger"
        pill
        role="button"
        tabIndex={0}
        onClick={() => navigate("/admin/system-data?entity=error-log")}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") navigate("/admin/system-data?entity=error-log");
        }}
      >
        {n}
      </Badge>
    </Hint>
  );
}
